import { apiService } from './api';

export interface CouponValidationResponse {
  valid: boolean;
  discount_percent?: number;
  discount_amount?: number;
  currency?: string;
  error_message?: string;
}

export interface CouponValidationRequest {
  coupon_code: string;
  tier: string;
  billing_cycle: 'monthly' | 'annual';
}

class CouponService {
  /**
   * Validate a coupon code and get discount information
   */
  async validateCoupon(request: CouponValidationRequest): Promise<CouponValidationResponse> {
    try {
      const response = await apiService.post('billing/validate-coupon', request);
      return response;
    } catch (error: any) {
      // Handle specific coupon validation errors
      if (error.response?.status === 400) {
        const detail = error.response?.data?.detail || error.response?.data?.message;
        return {
          valid: false,
          error_message: detail || 'Invalid coupon code'
        };
      }
      
      // Handle other errors
      return {
        valid: false,
        error_message: 'Unable to validate coupon code. Please try again.'
      };
    }
  }

  /**
   * Calculate discounted price
   */
  calculateDiscountedPrice(originalPrice: number, discountPercent: number): {
    original: number;
    discounted: number;
    savings: number;
  } {
    const discounted = Math.round(originalPrice * (1 - discountPercent / 100));
    const savings = originalPrice - discounted;
    
    return {
      original: originalPrice,
      discounted,
      savings
    };
  }

  /**
   * Format price for display
   */
  formatPrice(price: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  }
}

export const couponService = new CouponService();
